import React, { useState } from "react";
import { Checkbox, InputAdornment, MenuItem, TextField } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import LinkIcon from "@mui/icons-material/Link";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClock } from "@fortawesome/free-regular-svg-icons";
import CalendarDateInput from "../../../MyChamber/ChamberSettings/common/CalendarDateInput";
import { EventAgendaItem } from "../../eventMockData";
import {
  FieldLabel,
  WizardActionButton,
  WizardStepProps,
  moveWizardRow,
  normalizeWizardTimeInput,
  wizardGreenCheckboxSx,
  wizardId,
} from "./wizardShared";
import { SectionShell } from "../../../../common/dashboard/SectionShell";

// Options lifted from src/components/common/utilities/ProvincePicker.tsx (it
// does not export them); Canadian provinces first, then US states.
const provinceStateOptions = [
  { value: "AB", label: "Alberta" },
  { value: "BC", label: "British Columbia" },
  { value: "MB", label: "Manitoba" },
  { value: "NB", label: "New Brunswick" },
  { value: "NL", label: "Newfoundland and Labrador" },
  { value: "NS", label: "Nova Scotia" },
  { value: "NT", label: "Northwest Territories" },
  { value: "NU", label: "Nunavut" },
  { value: "ON", label: "Ontario" },
  { value: "PE", label: "Prince Edward Island" },
  { value: "QC", label: "Quebec" },
  { value: "SK", label: "Saskatchewan" },
  { value: "YT", label: "Yukon" },
  { value: "AL", label: "Alabama" },
  { value: "AK", label: "Alaska" },
  { value: "AZ", label: "Arizona" },
  { value: "AR", label: "Arkansas" },
  { value: "CA", label: "California" },
  { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" },
  { value: "DE", label: "Delaware" },
  { value: "FL", label: "Florida" },
  { value: "GA", label: "Georgia" },
  { value: "HI", label: "Hawaii" },
  { value: "ID", label: "Idaho" },
  { value: "IL", label: "Illinois" },
  { value: "IN", label: "Indiana" },
  { value: "IA", label: "Iowa" },
  { value: "KS", label: "Kansas" },
  { value: "KY", label: "Kentucky" },
  { value: "LA", label: "Louisiana" },
  { value: "ME", label: "Maine" },
  { value: "MD", label: "Maryland" },
  { value: "MA", label: "Massachusetts" },
  { value: "MI", label: "Michigan" },
  { value: "MN", label: "Minnesota" },
  { value: "MS", label: "Mississippi" },
  { value: "MO", label: "Missouri" },
  { value: "MT", label: "Montana" },
  { value: "NE", label: "Nebraska" },
  { value: "NV", label: "Nevada" },
  { value: "NH", label: "New Hampshire" },
  { value: "NJ", label: "New Jersey" },
  { value: "NM", label: "New Mexico" },
  { value: "NY", label: "New York" },
  { value: "NC", label: "North Carolina" },
  { value: "ND", label: "North Dakota" },
  { value: "OH", label: "Ohio" },
  { value: "OK", label: "Oklahoma" },
  { value: "OR", label: "Oregon" },
  { value: "PA", label: "Pennsylvania" },
  { value: "RI", label: "Rhode Island" },
  { value: "SC", label: "South Carolina" },
  { value: "SD", label: "South Dakota" },
  { value: "TN", label: "Tennessee" },
  { value: "TX", label: "Texas" },
  { value: "UT", label: "Utah" },
  { value: "VT", label: "Vermont" },
  { value: "VA", label: "Virginia" },
  { value: "WA", label: "Washington" },
  { value: "WV", label: "West Virginia" },
  { value: "WI", label: "Wisconsin" },
  { value: "WY", label: "Wyoming" },
];

const countryOptions = ["Canada", "United States"];

// Native time inputs emit HH:mm. Agenda rows keep storing display labels like
// "11:00 AM" because the event page renders timeLabel verbatim.
const toTwelveHourLabel = (value: string): string => {
  if (!value) return "";
  const [hours, minutes] = value.split(":").map(Number);
  const meridiem = hours >= 12 ? "PM" : "AM";
  const twelveHour = hours % 12 || 12;
  return `${twelveHour}:${String(minutes).padStart(2, "0")} ${meridiem}`;
};

// Styled to match the MUI size="small" outlined fields used across this step.
// The native picker indicator stays clickable but invisible; the overlaid
// clock icon renders in its place (EVT-CREATE-010) and the global
// NativeDatePickerClickOpener makes the whole field click-to-open.
const nativeTimeInputClassName =
  "h-10 w-full rounded border border-gray-300 bg-white px-3 pr-11 text-base text-gray-900 outline-none transition hover:border-gray-400 focus:border-cpaGold [&::-webkit-calendar-picker-indicator]:opacity-0";

const TimeInput = ({
  ariaLabel,
  value,
  onChange,
  error,
}: {
  ariaLabel: string;
  value: string;
  onChange: (value: string) => void;
  error?: boolean;
}): React.ReactElement => (
  <div className="relative">
    <input
      type="time"
      aria-label={ariaLabel}
      value={value}
      onChange={({ target }) => onChange(target.value)}
      className={`${nativeTimeInputClassName} ${
        error ? "border-red-500" : ""
      }`}
    />
    <FontAwesomeIcon
      icon={faClock}
      className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500"
    />
  </div>
);

const FieldError = ({ message }: { message?: string }): React.ReactElement | null =>
  message ? <p className="mt-2 text-sm text-red-600">{message}</p> : null;

const formatCardClassName = (selected: boolean) =>
  `flex cursor-pointer items-start gap-3 rounded-[10px] border bg-white px-5 py-4 transition ${
    selected ? "border-cpaGold" : "border-gray-300"
  }`;

// Event-wizard checkboxes are green (July 9, 2026 direction); the local
// wrapper only adds this step's padding override.
const formatCheckboxSx = { padding: 0.5, ...wizardGreenCheckboxSx };

const Step2DateLocation = ({
  event,
  setEvent,
  errors,
}: WizardStepProps): React.ReactElement => {
  const update = <K extends keyof typeof event>(key: K, value: (typeof event)[K]) =>
    setEvent((prev) => ({ ...prev, [key]: value }));
  // End Date is back (July 9, 2026 direction, reversing Q-010): it follows the
  // start date until the user picks their own end date, then stays put.
  const updateStartDate = (value: string) =>
    setEvent((prev) => ({
      ...prev,
      startDate: value,
      endDate:
        !prev.endDate || prev.endDate === prev.startDate ? value : prev.endDate,
    }));

  const agenda = event.agendaItems || [];
  const updateAgenda = (id: string, patch: Partial<EventAgendaItem>) =>
    update(
      "agendaItems",
      agenda.map((item) => (item.id === id ? { ...item, ...patch } : item))
    );
  // The mock's agenda rows carry six-dot drag handles — rows reorder by
  // dragging the handle (EVT-CREATE-012).
  const [agendaDragIndex, setAgendaDragIndex] = useState<number | null>(null);
  const dropAgendaRow = (targetIndex: number) => {
    if (agendaDragIndex === null || agendaDragIndex === targetIndex) {
      setAgendaDragIndex(null);
      return;
    }
    update("agendaItems", moveWizardRow(agenda, agendaDragIndex, targetIndex));
    setAgendaDragIndex(null);
  };

  const specialConsiderations = event.specialVenueConsiderations || "";

  return (
    <>
      <SectionShell
        id="event-date-location"
        variant="pill"
        title="Date & Location"
        description="Set the date, time, and location details for your event."
      >
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
          <div>
            <FieldLabel
              required
              hint="The date when your event will begin. Passed events will be removed from your calendar on your website 30 days after."
            >
              Start Date
            </FieldLabel>
            <CalendarDateInput
              mode="fullDate"
              ariaLabel="Start Date"
              placeholder="Select Date"
              value={event.startDate}
              onChange={updateStartDate}
            />
            <FieldError message={errors.startDate} />
          </div>
          <div>
            <FieldLabel
              required
              hint="The date when your event will end. Once the end date passes, the event is automatically unpublished from your website."
            >
              End Date
            </FieldLabel>
            <CalendarDateInput
              mode="fullDate"
              ariaLabel="End Date"
              placeholder="Select Date"
              value={event.endDate}
              onChange={(value) => update("endDate", value)}
            />
            <FieldError message={errors.endDate} />
          </div>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-10 lg:grid-cols-2">
          <div>
            <FieldLabel required hint="The time when your event will begin.">
              Start Time
            </FieldLabel>
            <TimeInput
              ariaLabel="Start Time"
              value={normalizeWizardTimeInput(event.startTime)}
              onChange={(value) => update("startTime", value)}
              error={Boolean(errors.startTime)}
            />
            <FieldError message={errors.startTime} />
          </div>
          <div>
            <FieldLabel required hint="The time when your event will end.">
              End Time
            </FieldLabel>
            <TimeInput
              ariaLabel="End Time"
              value={normalizeWizardTimeInput(event.endTime)}
              onChange={(value) => update("endTime", value)}
              error={Boolean(errors.endTime)}
            />
            <FieldError message={errors.endTime} />
          </div>
        </div>

        <div className="mt-12">
          <h3 className="text-[22px] font-extrabold leading-tight text-gray-950">
            Create Event Agenda
          </h3>
          <p className="mt-2 text-[18px] leading-7 text-gray-950">
            Build your event agenda by adding key activities and their start times. This will be displayed on your event page. If left blank it will not appear.
          </p>
        </div>

        <div className="mt-8 overflow-hidden rounded-[14px] border border-gray-200">
          <div className="grid grid-cols-[44px_30%_1fr_140px] bg-gray-50 px-8 py-5 text-xs font-extrabold uppercase tracking-wide text-black">
            <span className="col-span-2">START TIME</span>
            <span>DETAILS</span>
            <span className="text-center">ACTION</span>
          </div>
          {agenda.map((item, index) => (
            <div
              key={item.id}
              onDragOver={(dragEvent) => dragEvent.preventDefault()}
              onDrop={() => dropAgendaRow(index)}
              className={`grid grid-cols-[44px_30%_1fr_140px] items-center gap-4 border-t border-gray-200 px-8 py-5 ${
                agendaDragIndex === index ? "bg-gray-50" : ""
              }`}
            >
              <span
                draggable
                onDragStart={() => setAgendaDragIndex(index)}
                onDragEnd={() => setAgendaDragIndex(null)}
                className="cursor-grab"
              >
                <DragIndicatorIcon sx={{ color: "#111827", fontSize: 18 }} />
              </span>
              <TimeInput
                ariaLabel="Agenda start time"
                value={normalizeWizardTimeInput(item.timeLabel)}
                onChange={(value) =>
                  updateAgenda(item.id, { timeLabel: toTwelveHourLabel(value) })
                }
              />
              <TextField
                size="small"
                fullWidth
                value={item.details}
                onChange={({ target }) => updateAgenda(item.id, { details: target.value })}
              />
              <WizardActionButton
                tone="red"
                size="small"
                startIcon={<RemoveCircleOutlineIcon />}
                onClick={() =>
                  update(
                    "agendaItems",
                    agenda.filter((row) => row.id !== item.id)
                  )
                }
              >
                Remove
              </WizardActionButton>
            </div>
          ))}
        </div>

        <WizardActionButton
          className="!mt-9"
          tone="gold"
          startIcon={<AddIcon />}
          onClick={() =>
            update("agendaItems", [
              ...agenda,
              { id: wizardId("agenda"), timeLabel: "", details: "" },
            ])
          }
        >
          Add Agenda
        </WizardActionButton>
      </SectionShell>

      <SectionShell
        id="event-format"
        variant="pill"
        title="Select Event Format"
        description="Select one or both formats for your event. You can offer an in-person experience, a virtual experience, or both."
      >
        <FieldError message={errors.format} />
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <label className={formatCardClassName(event.inPersonEnabled)}>
            <Checkbox
              checked={event.inPersonEnabled}
              onChange={({ target }) => update("inPersonEnabled", target.checked)}
              sx={formatCheckboxSx}
            />
            <span>
              <span className="block text-[16px] font-extrabold leading-6 text-gray-950">
                In-person
              </span>
              <span className="mt-1 block text-[15px] leading-6 text-gray-500">
                This event has a venue or physical location.
              </span>
            </span>
          </label>
          <label className={formatCardClassName(event.virtualEnabled)}>
            <Checkbox
              checked={event.virtualEnabled}
              onChange={({ target }) => update("virtualEnabled", target.checked)}
              sx={formatCheckboxSx}
            />
            <span>
              <span className="block text-[16px] font-extrabold leading-6 text-gray-950">
                Virtual
              </span>
              <span className="mt-1 block text-[15px] leading-6 text-gray-500">
                Virtual event will require a meeting URL to be entered in text box to allow access
              </span>
            </span>
          </label>
        </div>

        {event.virtualEnabled ? (
          <div className="mt-8">
            <FieldLabel>Enter Meeting URL</FieldLabel>
            <TextField
              size="small"
              fullWidth
              placeholder="Enter your virtual meeting URL"
              value={event.virtualAccessInstructions}
              onChange={({ target }) => update("virtualAccessInstructions", target.value)}
              error={Boolean(errors.virtualAccessInstructions)}
              helperText={errors.virtualAccessInstructions}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LinkIcon sx={{ color: "#9ca3af", fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
            />
          </div>
        ) : null}

        {event.inPersonEnabled ? (
          <>
            <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
              <div>
                <FieldLabel
                  required
                  hint="The name of the venue where your event will take place."
                >
                  Venue Name
                </FieldLabel>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Venue Name"
                  value={event.venueName}
                  onChange={({ target }) => update("venueName", target.value)}
                  error={Boolean(errors.venueName)}
                  helperText={errors.venueName}
                />
              </div>
              <div>
                <FieldLabel required hint="The full address of the venue.">
                  Address
                </FieldLabel>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Street Number and Name"
                  value={event.address}
                  onChange={({ target }) => update("address", target.value)}
                  error={Boolean(errors.address)}
                  helperText={errors.address}
                />
              </div>
            </div>

            <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4">
              <div>
                <FieldLabel required hint="City where your event is located">
                  City
                </FieldLabel>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="City"
                  value={event.city || ""}
                  onChange={({ target }) => update("city", target.value)}
                />
              </div>
              <div>
                <FieldLabel required hint="State or province of the venue">
                  Province/State
                </FieldLabel>
                <TextField
                  size="small"
                  fullWidth
                  select
                  value={event.province || ""}
                  onChange={({ target }) => update("province", target.value)}
                  SelectProps={{
                    displayEmpty: true,
                    renderValue: (selected) => {
                      const value = selected as string;
                      if (!value) {
                        return <span className="text-gray-400">Province/State</span>;
                      }
                      return (
                        provinceStateOptions.find((option) => option.value === value)
                          ?.label || value
                      );
                    },
                  }}
                >
                  {provinceStateOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </div>
              <div>
                <FieldLabel required hint="Postal code of the venue">
                  Postal Code/Zip
                </FieldLabel>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Postal Code/Zip"
                  value={event.postalCode || ""}
                  onChange={({ target }) => update("postalCode", target.value)}
                />
              </div>
              <div>
                <FieldLabel required hint="Country where the event is located">
                  Country
                </FieldLabel>
                <TextField
                  size="small"
                  fullWidth
                  select
                  value={event.country || ""}
                  onChange={({ target }) => update("country", target.value)}
                  SelectProps={{
                    displayEmpty: true,
                    renderValue: (selected) => {
                      const value = selected as string;
                      return value ? (
                        value
                      ) : (
                        <span className="text-gray-400">Select Country</span>
                      );
                    },
                  }}
                >
                  {countryOptions.map((country) => (
                    <MenuItem key={country} value={country}>
                      {country}
                    </MenuItem>
                  ))}
                </TextField>
              </div>
            </div>
          </>
        ) : null}

        <div className="mt-10">
          <FieldLabel hint="Share any important details attendees should know, such as venue accessibility, parking instructions, elevator availability, or other relevant considerations.">
            Special Event Venue Considerations
          </FieldLabel>
          <TextField
            size="small"
            fullWidth
            multiline
            minRows={3}
            placeholder="Enter Details Here"
            value={specialConsiderations}
            onChange={({ target }) =>
              update("specialVenueConsiderations", target.value.slice(0, 500))
            }
            inputProps={{ maxLength: 500 }}
          />
          <p className="mt-2 text-[13px] leading-5 text-gray-500">
            {specialConsiderations.length}/500 characters
          </p>
        </div>
      </SectionShell>
    </>
  );
};

export default Step2DateLocation;
